import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { Sidebar } from 'semantic-ui-react';

// Import hooks.
import useWindowDimensions from '../../hooks/useWindowDimensions';

// Import helpers.
import WSA from '../../WSA';
import { isOrganizationSetupComplete } from '../../helpers/organizations';
import DesktopNavigation from '../../navigation/components/DesktopNavigation';
import FooterNavigation from '../../navigation/components/FooterNavigation';
import MirroredLeftNav from '../../navigation/components/MirroredLeftNav';
import { useMirroredLeftNav } from '../../navigation/components/useMirroredLeftNav';
import { openSidebar } from '../../redux/actions/layoutActions';
import { ImpersonateBanner } from '../Admin/Impersonation';
// Import components.
import { useScrollToTop } from '../../hooks/useScroll';
import TopNavigation from '../../navigation/components/TopNavigation';

const DesktopContainer = ({ isSidebarOpen, sliderOpen, user: _user, currentOrganization, openSidebar, children }) => {
  const location = useLocation();
  const { width } = useWindowDimensions();
  const { isReady, isAuthenticated } = WSA.Components.useWSAuth();
  const { isActive: useMirroredNav, isLoading: mirroredNavLoading } = useMirroredLeftNav();

  useScrollToTop({ id: 'content-sidebar-pusher' });

  const setupComplete = useMemo(() => {
    return isOrganizationSetupComplete(currentOrganization);
  }, [currentOrganization]);

  useEffect(() => {
    if (isReady && isAuthenticated && setupComplete) {
      location.pathname !== '/setup' && openSidebar(true);
    }
  }, [isAuthenticated, isReady, openSidebar, setupComplete, location]);

  // The desktop sidebar is a fixed width — it does not scale with the window.
  // Below the tablet breakpoint the layout switches to the mobile hamburger nav.
  const SIDEBAR_WIDTH = 275;
  const sidebarWidthSize = isSidebarOpen ? SIDEBAR_WIDTH : 0;
  const mainContentWidthSize = width - sidebarWidthSize;

  return (
    <Sidebar.Pushable>
      {useMirroredNav ? (
        <MirroredLeftNav variant="desktop" />
      ) : mirroredNavLoading ? null : (
        <DesktopNavigation
          visible={isSidebarOpen}
          style={{ width: sidebarWidthSize }}
          sidebarWidth={sidebarWidthSize}
        />
      )}
      <Sidebar.Pusher
        id="content-sidebar-pusher"
        style={{
          height: '100vh',
          width: mainContentWidthSize,
          transform: `translate3d(${sidebarWidthSize}px,0,0)`,
        }}
        dimmed={sliderOpen}
      >
        <div id="content" className={`${sliderOpen ? 'no-scroll' : ''}`}>
          <TopNavigation />

          <main id="main-content">
            <ImpersonateBanner />
            {children}
          </main>
        </div>

        <FooterNavigation />
      </Sidebar.Pusher>
    </Sidebar.Pushable>
  );
};

const mapStateToProps = (state) => ({
  currentOrganization: state.organizations.currentOrganization,
  isSidebarOpen: state.layout.isSidebarOpen,
  sliderOpen: state.layout.sliderOpen,
});

export default connect(mapStateToProps, { openSidebar })(DesktopContainer);
